import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration } from 'chart.js';

interface TrafficData {
  time?: string;
  entries: number;
  exits: number;
  location?: string;
  date?: string;
}

interface StatCard {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down';
  icon: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    BaseChartDirective
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  // Статистичні картки
  statCards: StatCard[] = [
    {
      title: 'Всього авто сьогодні',
      value: 1247,
      change: '+12.5%',
      trend: 'up',
      icon: 'directions_car'
    },
    {
      title: 'Активні сесії',
      value: 342,
      change: '+8.2%',
      trend: 'up',
      icon: 'local_parking'
    },
    {
      title: 'Заповненість',
      value: '68%',
      change: '-3.1%',
      trend: 'down',
      icon: 'assessment'
    },
    {
      title: 'Дохід (сьогодні)',
      value: '24 890 ₴',
      change: '+15.8%',
      trend: 'up',
      icon: 'account_balance_wallet'
    }
  ];

  // Трафік по годинах (демо-дані)
  hourlyTraffic: TrafficData[] = [
    { time: '00:00', entries: 12, exits: 8 },
    { time: '01:00', entries: 8, exits: 15 },
    { time: '02:00', entries: 5, exits: 7 },
    { time: '03:00', entries: 3, exits: 4 },
    { time: '04:00', entries: 6, exits: 5 },
    { time: '05:00', entries: 15, exits: 8 },
    { time: '06:00', entries: 45, exits: 12 },
    { time: '07:00', entries: 89, exits: 23 },
    { time: '08:00', entries: 134, exits: 45 },
    { time: '09:00', entries: 178, exits: 67 },
    { time: '10:00', entries: 156, exits: 89 },
    { time: '11:00', entries: 142, exits: 98 },
    { time: '12:00', entries: 167, exits: 112 },
    { time: '13:00', entries: 145, exits: 134 },
    { time: '14:00', entries: 189, exits: 145 },
    { time: '15:00', entries: 234, exits: 167 },
    { time: '16:00', entries: 267, exits: 189 },
    { time: '17:00', entries: 298, exits: 234 },
    { time: '18:00', entries: 245, exits: 267 },
    { time: '19:00', entries: 198, exits: 245 },
    { time: '20:00', entries: 167, exits: 198 },
    { time: '21:00', entries: 123, exits: 178 },
    { time: '22:00', entries: 89, exits: 134 },
    { time: '23:00', entries: 45, exits: 98 }
  ];

  // Трафік по днях тижня
  dailyTraffic: TrafficData[] = [
    { time: 'Пн', entries: 2345, exits: 2298 },
    { time: 'Вт', entries: 2456, exits: 2401 },
    { time: 'Ср', entries: 2567, exits: 2512 },
    { time: 'Чт', entries: 2678, exits: 2634 },
    { time: 'Пт', entries: 3123, exits: 3089 },
    { time: 'Сб', entries: 3456, exits: 3398 },
    { time: 'Нд', entries: 2890, exits: 2845 }
  ];

  // Трафік по точках в'їзду/виїзду
  locationTraffic: TrafficData[] = [
    { location: "В'їзд A (вул. Антоновича)", entries: 4523, exits: 0 },
    { location: "В'їзд B (з боку моста)", entries: 3876, exits: 0 },
    { location: "Виїзд A (на Антоновича)", entries: 0, exits: 4234 },
    { location: "Виїзд B (до моста)", entries: 0, exits: 4165 }
  ];

  displayedColumns: string[] = ['time', 'entries', 'exits', 'total'];
  displayedColumnsLocation: string[] = ['location', 'entries', 'exits', 'total'];

  // Конфігурація графіка по годинах
  public hourlyChartData: ChartConfiguration<'line'>['data'] = {
    labels: this.hourlyTraffic.map(d => d.time),
    datasets: [
      {
        data: this.hourlyTraffic.map(d => d.entries),
        label: 'В\'їзд',
        fill: true,
        tension: 0.4,
        borderColor: '#7b68ee',
        backgroundColor: 'rgba(123, 104, 238, 0.1)',
        pointBackgroundColor: '#7b68ee',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#7b68ee'
      },
      {
        data: this.hourlyTraffic.map(d => d.exits),
        label: "Виїзд",
        fill: true,
        tension: 0.4,
        borderColor: '#fa8072',
        backgroundColor: 'rgba(250, 128, 114, 0.1)',
        pointBackgroundColor: '#fa8072',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#fa8072'
      }
    ]
  };

  public hourlyChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(123, 104, 238, 0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Конфігурація графіка по днях
  public dailyChartData: ChartConfiguration<'bar'>['data'] = {
    labels: this.dailyTraffic.map(d => d.time),
    datasets: [
      {
        data: this.dailyTraffic.map(d => d.entries),
        label: 'В\'їзд',
        backgroundColor: '#7b68ee',
        borderColor: '#7b68ee',
        borderWidth: 2,
        borderRadius: 8,
      },
      {
        data: this.dailyTraffic.map(d => d.exits),
        label: 'Виїзд',
        backgroundColor: '#fa8072',
        borderColor: '#fa8072',
        borderWidth: 2,
        borderRadius: 8,
      }
    ]
  };

  public dailyChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(123, 104, 238, 0.1)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  ngOnInit(): void {
    console.log('Dashboard initialized with Ocean Plaza statistics');
  }

  getTotal(item: TrafficData): number {
    return item.entries + item.exits;
  }
}
